import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash2, ShieldX } from "lucide-react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../lib/convexApi";
import { ENTITY_TYPES } from "@agentguard/shared-lib/entityTypes";
import { PageHeader } from "../PageHeader";
import { FullGraphViz } from "../shared/FullGraphViz";
import { NewNoteModal } from "../shared/NewNoteModal";
import { renderMarkdownWithWikilinks } from "../../../markdown";

// === Canonical entity-type chips ============================================
// Sourced from shared-lib (`@agentguard/shared-lib/entityTypes`) so the
// chip values cannot drift from the backend canonical list. Per
// [[feedback-pr46-review-patterns]] #13 a rename in shared-lib ripples
// to UI in lockstep — no parallel string literal.
// ============================================================================
const TYPE_LABELS: Record<string, string> = {
  "security-note": "Security note",
  "policy-decision": "Policy decision",
  "dlp-hit": "DLP hit",
  "approval-resolved": "Approval resolved",
  "pr-risk-brief": "PR risk brief",
};

export const MEMORY_TYPE_CHIPS: ReadonlyArray<{ value: string; label: string }> = Object.freeze(
  ENTITY_TYPES.map((value) => ({
    value,
    label: TYPE_LABELS[value] ?? value,
  }))
);

interface GraphEntity {
  id: string;
  type: string;
  name: string;
  isPrivate: boolean;
  metadata: Record<string, unknown>;
  createdAt: number;
  createdBy: string;
}

interface GraphRelation {
  id: string;
  from: string;
  to: string;
  relationType: string;
  createdAt: number;
}

interface GraphResult {
  entities: GraphEntity[];
  relations: GraphRelation[];
}

interface DisplayNote {
  id: string;
  title: string;
  when: string;
  body: string;
  related: Array<{ id: string; title: string }>;
  entityType?: string;
  isPrivate?: boolean;
  isMine?: boolean;
}

// Project the Convex graph shape onto the rich display shape the existing
// UI components consume (list item + ego graph viz). The legacy REST
// payload included body/related as pre-rendered strings; the Convex
// shape gives us raw metadata + relations and we derive the rest here
// in the component so the response is reactive (changes ripple without
// a refetch).
function projectGraphForDisplay(graph: GraphResult): DisplayNote[] {
  const byId = new Map(graph.entities.map((e) => [e.id, e]));
  // Relations are indexed in BOTH directions. Only outgoing edges used to
  // count, so a note that others link TO reported "0 links" while the graph
  // beside it visibly drew those edges and the detail pane rendered them as
  // wikilinks. From a reader's point of view a link is a link; direction is an
  // artifact of who happened to write the note first.
  const relsByNode = new Map<string, Array<{ other: string }>>();
  const push = (node: string, other: string) => {
    const bucket = relsByNode.get(node) ?? [];
    bucket.push({ other });
    relsByNode.set(node, bucket);
  };
  for (const r of graph.relations) {
    push(r.from, r.to);
    if (r.to !== r.from) push(r.to, r.from);
  }
  return graph.entities.map((e) => {
    const meta = (e.metadata ?? {}) as Record<string, unknown>;
    const body =
      typeof meta.body === "string"
        ? meta.body
        : typeof meta.summary === "string"
          ? meta.summary
          : "";
    const title = typeof meta.title === "string" ? meta.title : e.name;
    const when = new Date(e.createdAt).toISOString().slice(0, 10);
    const seen = new Set<string>();
    const related = (relsByNode.get(e.id) ?? [])
      .map((r) => {
        const target = byId.get(r.other);
        if (!target) return null;
        // A self-referencing relation (from === to) would list the note as
        // related to itself, which reads as a real link an operator would try
        // to follow.
        if (target.id === e.id) return null;
        // A pair linked in both directions is still one link.
        if (seen.has(target.id)) return null;
        seen.add(target.id);
        const targetMeta = (target.metadata ?? {}) as Record<string, unknown>;
        const targetTitle = typeof targetMeta.title === "string" ? targetMeta.title : target.name;
        return { id: target.id, title: targetTitle };
      })
      .filter((x): x is { id: string; title: string } => x !== null);
    return {
      id: e.id,
      title,
      when,
      body,
      related,
      entityType: e.type,
      isPrivate: e.isPrivate,
      // The Convex readGraph projection strips createdBy comparison —
      // ACL already excluded other users' private rows, so anything
      // marked private that survived must be mine.
      isMine: e.isPrivate ? true : undefined,
    };
  });
}

export function MemoryPage() {
  // Reactive read. Convex pushes updates when entities/relations change
  // in this org — Memory page reflects them with no manual refresh per
  // spec §5 reactive subscription model. PR #60 made `api` both a type
  // AND a runtime value, so the `(api as any)` cast that the original
  // commit needed is no longer required.
  //
  // Per [[feedback-spec5-pr54-64-review-patterns]] #14 the reactive
  // snapshot is preserved exactly as Spec 5 PR 3 wired it — Spec 5.1
  // PR 1 ONLY adds the appendNote mutation below. Do not refactor this
  // useQuery in this PR (notesForGraph exists as a follow-up option
  // but the display projection here is the source of truth).
  const graph = useQuery(api.memory.queries.readGraph, {}) as GraphResult | undefined;
  // Spec 5.1 PR 1 — appendNote replaces the legacy createMemoryNote()
  // REST call. The mutation is plan:security gated server-side so the
  // UI doesn't have to do its own 402 handling.
  const appendNote = useMutation(api.memory.notesMutations.appendNote);
  // Two-tier delete, matching the two mutations' own gating. Remove is a
  // tombstone any security-tier member may set on a note they can see; purge is
  // the irreversible compliance erase and is admin-only server-side. Both are
  // re-checked in Convex — the gate below decides what to SHOW, never what is
  // allowed.
  const softDeleteEntity = useMutation(api.memory.mutations.softDeleteEntity);
  const purgeEntity = useMutation(api.memory.mutations.purgeEntity);
  // Same admin signal the Admin Console and the exceptions drawer read. While
  // the query is unresolved this is false, so the purge button appears when the
  // role arrives rather than flashing and disappearing.
  const myRole = useQuery(api.billing.queries.myRole, {}) as { isAdmin?: boolean } | undefined;
  const isOrgAdmin = myRole?.isAdmin === true;

  const notes: DisplayNote[] = useMemo(() => {
    if (!graph) return [];
    return projectGraphForDisplay(graph);
  }, [graph]);

  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string | null>(null); // null = "All"
  const [showPrivate, setShowPrivate] = useState(false);
  // Bound to the note it happened on. A bare message string stayed on screen
  // after switching notes, so "admin role required" from note A appeared to be
  // about note B.
  const [deleteError, setDeleteError] = useState<{ id: string; message: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Both handlers confirm first, then let the mutation's own guard decide.
  // A member who somehow reaches purge gets a 403 surfaced here rather than a
  // silent no-op — the UI never pretends a refused action succeeded.
  async function runDelete(
    note: DisplayNote,
    prompt: string,
    mutate: (args: { id: string }) => Promise<unknown>
  ) {
    if (!window.confirm(prompt)) return;
    setBusyId(note.id);
    setDeleteError(null);
    try {
      await mutate({ id: note.id });
      // No local list surgery: readGraph is a live subscription, so the row
      // leaves the list on the server's next push. The activeId effect above
      // then falls through to the first remaining note.
    } catch (err) {
      setDeleteError({
        id: note.id,
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusyId(null);
    }
  }

  // Initialize / preserve activeId once notes land. When notes change
  // (Convex pushes an update) and the previously-active row is gone,
  // fall through to the first visible row.
  useEffect(() => {
    if (notes.length === 0) {
      setActiveId(null);
      return;
    }
    if (!activeId || !notes.find((n) => n.id === activeId)) {
      setActiveId(notes[0].id);
    }
  }, [notes, activeId]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      const anchor = (e.target as HTMLElement | null)?.closest?.(
        "a.wikilink"
      ) as HTMLElement | null;
      if (anchor) {
        e.preventDefault();
        const target = anchor.getAttribute("data-target");
        if (!target) return;
        const found = notes.find((n) => n.title.toLowerCase() === target.toLowerCase());
        if (found) setActiveId(found.id);
      }
    }
    const body = bodyRef.current;
    if (body) body.addEventListener("click", onClick);
    return () => {
      if (body) body.removeEventListener("click", onClick);
    };
  }, [notes]);

  // Client-side filter is PRESENTATION ONLY. The backend ACL filter (spec §5.5)
  // already strips out other users' private entries; we do NOT use this
  // filter to gate visibility. The "Show private" toggle controls whether
  // MY OWN private notes show inline with the org-public ones.
  const filtered = useMemo(() => {
    return notes.filter((n) => {
      if (typeFilter && n.entityType && n.entityType !== typeFilter) return false;
      if (!showPrivate && n.isPrivate) return false;
      if (search && !n.title.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [notes, typeFilter, showPrivate, search]);

  const active = filtered.find((n) => n.id === activeId) || filtered[0];

  // Stable identities for the graph props. Computing these inline in JSX
  // allocated fresh arrays every render, which defeated FullGraphViz's
  // `useMemo([nodes, edges])` and re-ran its 220-iteration O(n²) force sim on
  // every keystroke / reactive Convex push. Memoize on `filtered` so the
  // layout only recomputes when the visible note set actually changes.
  const graphNodes = useMemo(
    () =>
      filtered.map((n) => ({
        id: n.id,
        label: n.title,
        type: n.entityType,
        isPrivate: n.isPrivate,
      })),
    [filtered]
  );
  const graphEdges = useMemo(
    () =>
      filtered.flatMap((n) =>
        n.related
          .filter((r) => filtered.some((f) => f.id === r.id))
          .map((r) => ({ from: n.id, to: r.id }))
      ),
    [filtered]
  );

  const loading = graph === undefined;

  return (
    <>
      <PageHeader
        title="Memory graph"
        subtitle="wikilinked notes — context across sessions"
        actions={
          <button type="button" className="sr-btn" onClick={() => setModalOpen(true)}>
            <Plus size={14} /> New note
          </button>
        }
      />
      <div className="sr-mem-filters">
        <button
          type="button"
          className={`sr-chip${typeFilter === null ? " is-active" : ""}`}
          onClick={() => setTypeFilter(null)}
        >
          All
        </button>
        {MEMORY_TYPE_CHIPS.map((chip) => (
          <button
            key={chip.value}
            type="button"
            className={`sr-chip${typeFilter === chip.value ? " is-active" : ""}`}
            onClick={() => setTypeFilter(chip.value)}
            data-value={chip.value}
          >
            {chip.label}
          </button>
        ))}
        <label className="sr-toggle">
          <input
            type="checkbox"
            checked={showPrivate}
            onChange={(e) => setShowPrivate(e.target.checked)}
            aria-label="Show private"
          />
          <span>Show private</span>
        </label>
      </div>
      {loading ? (
        <div className="sr-page-body">
          <div className="sr-empty">Loading memory graph…</div>
        </div>
      ) : (
        <div className="sr-mem-layout">
          <aside className="sr-mem-list">
            <input
              className="sr-mem-search"
              placeholder="Search notes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {filtered.map((n) => (
              <button
                key={n.id}
                type="button"
                className={`sr-mem-listitem${n.id === activeId ? " is-active" : ""}`}
                onClick={() => setActiveId(n.id)}
              >
                <strong>
                  {n.title}
                  {n.isPrivate && <span className="sr-private-badge">private</span>}
                </strong>
                <small>
                  {n.related.length} links · {n.when}
                  {n.entityType ? ` · ${n.entityType}` : ""}
                </small>
              </button>
            ))}
            {filtered.length === 0 && <div className="sr-empty">No notes match.</div>}
          </aside>

          <div className="sr-mem-viz-pane">
            <FullGraphViz
              nodes={graphNodes}
              edges={graphEdges}
              activeId={activeId}
              onSelect={setActiveId}
            />
          </div>

          <section className="sr-mem-detail" ref={bodyRef}>
            {active && (
              <>
                <h2>
                  {active.title}
                  {active.isPrivate && <span className="sr-private-badge">private</span>}
                </h2>
                <div className="sr-mem-detail__meta">
                  <span>{active.when}</span>
                  <span className="sr-mem-detail__actions">
                    <button
                      type="button"
                      className="sr-btn sr-btn--danger"
                      disabled={busyId === active.id}
                      onClick={() =>
                        runDelete(
                          active,
                          `Remove "${active.title}" from the memory graph?\n\nThe note and its observations stop appearing in the graph, in search and in agent reads. The row is kept as a tombstone rather than erased, so an administrator can still purge it permanently. There is no in-app undo.`,
                          softDeleteEntity
                        )
                      }
                    >
                      <Trash2 size={14} /> Remove
                    </button>
                    {isOrgAdmin && (
                      <button
                        type="button"
                        className="sr-btn sr-btn--danger"
                        disabled={busyId === active.id}
                        onClick={() =>
                          runDelete(
                            active,
                            `Permanently purge "${active.title}"?\n\nThis erases the note, every observation attached to it, and every relation touching it — from disk, not just from view. It cannot be undone. An audit event records the purge (id, type and counts; not the note's name or content).`,
                            purgeEntity
                          )
                        }
                      >
                        <ShieldX size={14} /> Purge
                      </button>
                    )}
                  </span>
                </div>
                {deleteError?.id === active.id && (
                  <div className="sr-empty">&#9888; {deleteError.message}</div>
                )}
                <div
                  className="sr-mem-detail__body"
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdownWithWikilinks(active.body),
                  }}
                />
              </>
            )}
          </section>
        </div>
      )}

      <NewNoteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreate={async (input) => {
          // Spec 5.1 PR 1: writes go directly to the Convex public
          // mutation. The useQuery(api.memory.queries.readGraph) above
          // reactively picks up the new entity in the same render cycle —
          // no shadow-write hop through the legacy REST route. Plan
          // gating (plan:security) is enforced server-side; a 402-equiv
          // ConvexError bubbles up here as a thrown Error which the
          // NewNoteModal already catches via its try/finally.
          await appendNote({ title: input.title, body: input.body });
        }}
      />
    </>
  );
}
